import React from 'react';
import _ from 'lodash';
import styled, { css } from 'styled-components';

import { getCategoryLabels } from 'src/dispensary-admin/customize/customize.utils';
import { useDispensary } from 'src/hooks/use-dispensary';
import { CircleWithCheckMark } from 'src/svg/circle-with-checkmark';
import { ChevronIcon } from 'src/svg/chevron-icon';
import { VisuallyHidden } from 'shared/components/visually-hidden';
import Button from 'shared/modals/button';
import { Category } from '../categories.types';
import { useSelectImageModal } from './use-select-image-modal';
import { ModalScreen } from './modal-screen';
import { LIBRARY_PER_PAGE } from './constants';

type ImageLibraryProps = {
  category: Category;
  saveImage: () => void;
  openUploadImageModal: () => void;
  selectedImage: string;
  setSelectedImage: (image: string) => void;
};

export const ImageLibrary = ({
  category,
  saveImage,
  openUploadImageModal,
  selectedImage,
  setSelectedImage,
}: ImageLibraryProps): JSX.Element => {
  const { category: value } = category;
  const dispensary = useDispensary();
  const categoryLabels = getCategoryLabels(dispensary.location?.state);
  const categoryName = categoryLabels?.[value] ?? value;

  const { photos, carouselRef, isPrevBtnEnabled, isNextBtnEnabled, scrollPrev, scrollNext } = useSelectImageModal({
    category,
    selectedImage,
  });

  const chunkedPhotos = _.chunk(photos, LIBRARY_PER_PAGE);

  return (
    <ModalScreen
      title={`${categoryName} Image`}
      description='Select a photo from the dutchie library or upload your own.'
    >
      <Wrapper>
        <Embla ref={carouselRef}>
          <Container>
            {chunkedPhotos.map((chunk) => (
              <Slide>
                <Photos>
                  {chunk.map((photo) => (
                    <Photo key={photo} $selected={selectedImage === photo} onClick={() => setSelectedImage(photo)}>
                      <img src={photo} alt={photo} />
                      {selectedImage === photo && <SelectedCheckmark />}
                    </Photo>
                  ))}
                </Photos>
              </Slide>
            ))}
          </Container>
        </Embla>

        {isPrevBtnEnabled && (
          <PrevButton onClick={scrollPrev}>
            <VisuallyHidden>Previous</VisuallyHidden>
            <PrevArrow />
          </PrevButton>
        )}

        {isNextBtnEnabled && (
          <NextButton onClick={scrollNext}>
            <VisuallyHidden>Next</VisuallyHidden>
            <NextArrow />
          </NextButton>
        )}
      </Wrapper>

      <ButtonContainer>
        <Button inverted buttonContainerWidth='auto' mt='0' onClick={openUploadImageModal}>
          Upload Image
        </Button>
        <Button buttonContainerWidth='auto' mt='0' onClick={() => saveImage()}>
          Save Image
        </Button>
      </ButtonContainer>
    </ModalScreen>
  );
};

const Wrapper = styled.div`
  position: relative;
  width: 100%;
`;

const Embla = styled.div`
  margin: 24px auto;
  overflow: hidden;
  max-width: 680px;
  width: 100%;
`;

const Container = styled.div`
  display: flex;
  will-change: transform;
`;

const Slide = styled.div`
  flex: 0 0 100%;
  min-width: 0;
  padding: 16px;
`;

const Photos = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`;

const Photo = styled.button<{ $selected: boolean }>`
  align-items: center;
  appearance: none;
  background: #fff;
  border: 2px solid;
  border-color: ${({ $selected }) => ($selected ? '#4ca667' : '#fff')};
  border-radius: 16px;
  box-shadow: 0 4px 10px 0 #00000019;
  cursor: pointer;
  display: flex;
  height: 150px;
  overflow: hidden;
  padding: 0;
  position: relative;
  width: 150px;

  img {
    display: block;
  }
`;

const SelectedCheckmark = styled(CircleWithCheckMark)`
  position: absolute;
  right: 12px;
  top: 12px;
`;

const carouselButton = css`
  align-items: center;
  background: #fff;
  border: none;
  border-radius: 50%;
  box-shadow: 0 3px 5px 0 #0000002b;
  color: #4f5d68;
  cursor: pointer;
  display: flex;
  height: 48px;
  justify-content: center;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 48px;
`;

const PrevButton = styled.button`
  ${carouselButton};

  left: 0;
`;

const NextButton = styled.button`
  ${carouselButton};

  right: 0;
`;

const PrevArrow = styled(ChevronIcon)`
  transform: rotate(90deg);
  width: 18px;
`;

const NextArrow = styled(ChevronIcon)`
  transform: rotate(-90deg);
  width: 18px;
`;

const ButtonContainer = styled.div`
  align-items: center;
  display: flex;
  gap: 16px;
  justify-content: center;
  margin-top: 0;
  width: 100%;
`;
